import { useState, useEffect, useCallback } from 'react';
import { fetchSeasons, fetchWeeks, fetchGames, fetchPlays } from './api';
import SeasonsView from './views/seasons-view';
import WeeksView from './views/weeks-view';
import GamesView from './views/games-view';
import PlaysView from './views/plays-view';

function isFutureGame(game) {
  return !!game.scheduledTime && new Date(game.scheduledTime) > new Date();
}

function updateUrl(params) {
  const search = new URLSearchParams(params).toString();
  window.history.pushState(null, '', search ? `?${search}` : '/');
}

function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    season: params.get('season') ? parseInt(params.get('season'), 10) : null,
    week: params.get('week') ? parseInt(params.get('week'), 10) : null,
    game: params.get('game') || null,
    view: params.get('view') || null,
  };
}

export default function App() {
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [weeks, setWeeks] = useState({ regularSeason: [], postseason: [] });
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [plays, setPlays] = useState([]);
  const [showGraph, setShowGraph] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const restoreFromParams = useCallback((seasonsList, params) => {
    if (!params.season) {
      setLoading(false);
      return;
    }

    const season = seasonsList.find(s => s.year === params.season);
    if (!season) {
      setLoading(false);
      return;
    }

    setSelectedSeason(season);

    fetchWeeks(season.year)
      .then(weeksData => {
        const regularSeason = weeksData.regularSeason || [];
        const postseason = weeksData.postseason || [];
        setWeeks({ regularSeason, postseason });

        if (!params.week) {
          setLoading(false);
          return;
        }

        const allWeeks = [...regularSeason, ...postseason];
        const week = allWeeks.find(w => w.week === params.week);
        if (!week) {
          setLoading(false);
          return;
        }

        setSelectedWeek(week);

        return fetchGames(season.year, week.week).then(gamesData => {
          setGames(gamesData.games);

          if (!params.game) {
            setLoading(false);
            return;
          }

          const game = gamesData.games.find(g => g.gameId === params.game);
          if (!game) {
            setLoading(false);
            return;
          }

          setSelectedGame(game);
          setShowGraph(params.view === 'graph');

          if (isFutureGame(game)) {
            setPlays([]);
            setLoading(false);
            return;
          }

          return fetchPlays(game.gameId).then(playsData => {
            setPlays(playsData.plays);
            setLoading(false);
          });
        });
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchSeasons()
      .then(data => {
        const sorted = [...data.seasons].sort((a, b) => b.year - a.year);
        setSeasons(sorted);

        const params = getUrlParams();
        if (params.season) {
          restoreFromParams(sorted, params);
        } else {
          setLoading(false);
        }
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [restoreFromParams]);

  useEffect(() => {
    function handlePopState() {
      const params = getUrlParams();

      setError(null);
      setPlays([]);
      setSelectedGame(null);
      setGames([]);
      setSelectedWeek(null);
      setWeeks({ regularSeason: [], postseason: [] });
      setSelectedSeason(null);

      if (params.season && seasons.length > 0) {
        setLoading(true);
        restoreFromParams(seasons, params);
      }
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [seasons, restoreFromParams]);

  function selectSeason(season) {
    setSelectedSeason(season);
    setWeeks({ regularSeason: [], postseason: [] });
    setSelectedWeek(null);
    setGames([]);
    setSelectedGame(null);
    setPlays([]);
    setLoading(true);
    setError(null);
    updateUrl({ season: season.year });

    fetchWeeks(season.year)
      .then(data => setWeeks({ regularSeason: data.regularSeason || [], postseason: data.postseason || [] }))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }

  function selectWeek(week) {
    setSelectedWeek(week);
    setGames([]);
    setSelectedGame(null);
    setPlays([]);
    setLoading(true);
    setError(null);
    updateUrl({ season: selectedSeason.year, week: week.week });

    fetchGames(selectedSeason.year, week.week)
      .then(data => setGames(data.games))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }

  function selectGame(game) {
    setSelectedGame(game);
    setPlays([]);
    setShowGraph(true);
    setLoading(true);
    setError(null);
    updateUrl({ season: selectedSeason.year, week: selectedWeek.week, game: game.gameId, view: 'graph' });

    if (isFutureGame(game)) {
      setLoading(false);
      return;
    }

    fetchPlays(game.gameId)
      .then(data => setPlays(data.plays))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }

  function backToSeasons() {
    setSelectedSeason(null);
    setWeeks({ regularSeason: [], postseason: [] });
    setSelectedWeek(null);
    setGames([]);
    setSelectedGame(null);
    setPlays([]);
    setError(null);
    updateUrl({});
  }

  function backToWeeks() {
    setSelectedWeek(null);
    setGames([]);
    setSelectedGame(null);
    setPlays([]);
    setError(null);
    updateUrl({ season: selectedSeason.year });
  }

  function backToGames() {
    setSelectedGame(null);
    setPlays([]);
    setShowGraph(false);
    setError(null);
    updateUrl({ season: selectedSeason.year, week: selectedWeek.week });
  }

  function toggleGraph() {
    const next = !showGraph;
    setShowGraph(next);
    updateUrl({ season: selectedSeason.year, week: selectedWeek.week, game: selectedGame.gameId, view: next ? 'graph' : 'plays' });
  }

  let view;

  if (selectedSeason && selectedWeek && selectedGame) {
    view = (
      <PlaysView
        game={selectedGame}
        plays={plays}
        showGraph={showGraph}
        onToggleGraph={toggleGraph}
        loading={loading}
        error={error}
        onBack={backToGames}
      />
    );
  } else if (selectedSeason && selectedWeek) {
    view = (
      <GamesView
        season={selectedSeason}
        week={selectedWeek}
        games={games}
        loading={loading}
        error={error}
        onSelectGame={selectGame}
        onBack={backToWeeks}
      />
    );
  } else if (selectedSeason) {
    view = (
      <WeeksView
        season={selectedSeason}
        weeks={weeks}
        loading={loading}
        error={error}
        onSelectWeek={selectWeek}
        onBack={backToSeasons}
      />
    );
  } else {
    view = (
      <SeasonsView
        seasons={seasons}
        loading={loading}
        error={error}
        onSelectSeason={selectSeason}
      />
    );
  }

  return (
    <div className="page">
      <header className="site-header">
        <h1>UsefulChaos Football Win Probability</h1>
      </header>
      {view}
      <footer className="site-footer">
        Copyright - UsefulChaos.com
      </footer>
    </div>
  );
}
